import { skyJourneyMemberCounts, skyJourneyWavePolicy } from './111-sky-approach-waves.mjs';
import { markSkyJourneyDeparture } from './107-sky-rewards.mjs';
import {
  SKY_ENEMY_CLASSES,
  SKY_FORMATIONS,
  skyJourneyChapter,
  skySquadClass,
  skySquadDisplacement,
} from './78-sky-combat.mjs';
import { createSkyBonusTarget, createSkyObstacles } from './82-sky-bonuses.mjs';
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

/** A finite authored journey ledger. Seconds are active simulation time; kills,
 * never elapsed approach time, release the next formation and guardian. */
export function createSkyJourney(sector, stage) {
  const memberCounts = skyJourneyMemberCounts(stage),
    waveCount = memberCounts.length;
  return {
    sector,
    phase: 'waves',
    waveCount,
    memberCounts,
    launched: 0,
    cleared: 0,
    firstPassMarks: Array(waveCount).fill(null),
    masteryEligible: waveCount > 0,
    defeated: 0,
    requiredKills: memberCounts.reduce((sum, count) => sum + count, 0),
    activeSquad: null,
    wait: 0,
    returnCount: 0,
    lastReturnAt: null,
    grazedThreats: [],
  };
}
/** Advance one kill-gated wave. Call once per fixed tick before enemy movement.
 * Explicit boss-only starts bypass admission with an unearned, skipped ledger.
 * The caller retains all damage/reward authority and supplies its ID/event owners. */
export function stepSkyJourney(s, stage, dt, { spawn, event }) {
  if (!s.journey || s.journey.sector !== s.sector) {
    s.journey = createSkyJourney(s.sector, stage);
    if (s.startMode === 'boss') {
      // Testing admission is explicit: no invented cleared waves, kills or mastery.
      s.journey.phase = 'boss';
      s.journey.skipped = true;
      s.journey.masteryEligible = false;
    }
  }
  const j = s.journey;
  if (j.phase === 'boss') return true;
  const squad = s.squads.find((value) => value.id === j.activeSquad);
  j.defeated =
    j.memberCounts.slice(0, j.cleared).reduce((sum, count) => sum + count, 0) + (squad?.kills ?? 0);
  if (squad?.kills === squad?.total && squad) {
    j.cleared++;
    j.activeSquad = null;
    j.wait = j.cleared === j.waveCount ? 2 : 0.9;
    if (j.cleared === j.waveCount) {
      j.phase = 'handoff';
      s.threats = [];
      s.grace = Math.max(s.grace, 2);
      if (s.bossSupercharge?.status === 'ready')
        event(s, 'boss-supercharge-ready', 'AIRSPACE CLEAR · BOSS SUPERCHARGE READY');
      else event(s, 'journey-clear', 'AIRSPACE CLEAR · GUARDIAN APPROACHING');
    }
  }
  if (j.wait > 0) {
    j.wait = Math.max(0, j.wait - dt);
    return false;
  }
  if (j.phase === 'handoff') {
    j.phase = 'boss';
    return true;
  }
  if (j.activeSquad === null && j.launched < j.waveCount) {
    const chapter = skyJourneyChapter(stage, j.launched);
    j.activeSquad = spawnSquad(s, stage, chapter, spawn);
    s.wave = j.launched++;
    s.chapterIndex = chapter.index;
    s.chapterLabel = chapter.name;
    s.chapterTotal = j.waveCount;
    const bonus = s.journeyAltitude ? null : createSkyBonusTarget(s, chapter.index);
    if (bonus) s.enemies.push(bonus);
    event(s, 'chapter', `${j.launched}/${j.waveCount} · ${chapter.name}`);
  }
  return false;
}

/** Surviving craft take a real exterior loop back to the same formation lane.
 * Identity, remaining HP and kill/reward counters are preserved. Returning craft
 * cannot shoot while turning, but remain ordinary physical projectile targets.
 * Each visible re-entry rearms its telegraphed volleys. Stable graze keys, rather
 * than harmless exhausted enemies, bound repeat rewards across these passes. */
export function stepSkyJourneyReturn(s, enemy, dt, event) {
  if (!enemy.journeyMember || enemy.hp <= 0) return false;
  markSkyJourneyDeparture(s, enemy);
  if (!enemy.journeyReturn && enemy.z >= 6) {
    const shift = skySquadDisplacement(enemy, 0),
      side = enemy.slot % 2 ? 1 : -1;
    const end = {
      x: enemy.centerX + enemy.offsetX + shift.x,
      y: enemy.centerY + enemy.offsetY + shift.y,
      z: -70 + enemy.offsetZ + shift.z,
    };
    enemy.journeyReturn = {
      age: 0,
      points: [
        { x: enemy.x, y: enemy.y, z: enemy.z },
        { x: side * 22, y: 12 + (s.journeyAltitude?.originY ?? 0), z: 12 },
        { x: side * 22, y: 12 + (s.journeyAltitude?.originY ?? 0), z: -100 },
        end,
      ],
    };
    const j = s.journey;
    if (j) {
      j.returnCount++;
      if (j.lastReturnAt === null || s.time - j.lastReturnAt > 1)
        event(s, 'formation-return', 'SURVIVORS TURNING BACK · FINISH THE FORMATION');
      j.lastReturnAt = s.time;
    }
  }
  const loop = enemy.journeyReturn;
  if (!loop) return false;
  loop.age += dt;
  enemy.animationOffset = (enemy.animationOffset ?? 0) + dt;
  const t = clamp(loop.age / 6, 0, 1),
    u = 1 - t,
    p = loop.points;
  for (const axis of ['x', 'y', 'z']) {
    enemy[axis] =
      u * u * u * p[0][axis] +
      3 * u * u * t * p[1][axis] +
      3 * u * t * t * p[2][axis] +
      t * t * t * p[3][axis];
    enemy['v' + axis] =
      (3 * u * u * (p[1][axis] - p[0][axis]) +
        6 * u * t * (p[2][axis] - p[1][axis]) +
        3 * t * t * (p[3][axis] - p[2][axis])) /
      6;
  }
  enemy.vy += enemy.journeyClimbVelocity ?? 0;
  enemy.charge = 0;
  enemy.attackPhase = 'return';
  enemy.formationBank = clamp(-enemy.vx * 0.04, -0.7, 0.7);
  if (t === 1) {
    enemy.journeyReturn = null;
    enemy.animationOffset += enemy.age;
    enemy.age = 0;
    enemy.firedShots = 0;
    enemy.fired = false;
    enemy.chargeAge = 0;
  }
  return true;
}
/** Claim one graze reward per authored member/volley/fan slot, across all retries.
 * Ordinary/guardian bolts retain their existing reward rules. The ledger is at
 * most required-member count ×3 volleys ×3 fan slots; malformed or excess
 * keys fail closed, including the larger opening-stage swarm formations. */
export function claimSkyJourneyGraze(state, bolt) {
  if (bolt.journeyGrazeKey === undefined) return true;
  const j = state.journey,
    key = bolt.journeyGrazeKey;
  if (!j || typeof key !== 'string' || !/^\d+:[1-3]:[0-2]$/.test(key)) return false;
  const ledger = j.grazedThreats;
  if (!Array.isArray(ledger) || ledger.includes(key) || ledger.length >= j.requiredKills * 9)
    return false;
  ledger.push(key);
  return true;
}
function spawnSquad(s, stage, chapter, spawn) {
  const profile = SKY_ENEMY_CLASSES[chapter.enemyClass];
  const wave = skyJourneyWavePolicy(stage, s.journey.launched);
  const formation = SKY_FORMATIONS[chapter.formation];
  const offsets = Array.from({ length: wave.members }, (_, slot) => {
    const [x, y, z] = formation[slot % formation.length];
    return [x, y, z - Math.floor(slot / formation.length) * wave.rowDepth];
  });
  const phase = (s.seed % 97) * 0.13 + chapter.index * 1.9 + s.sector;
  const centerX = Math.sin(phase) * 5.5;
  const centerY = 4.7 + Math.cos(phase) * 0.6 + (s.journeyAltitude?.originY ?? 0);
  const squad = s.nextId;
  s.squads.push({
    id: squad,
    total: offsets.length,
    kills: 0,
    precision: 0,
    escaped: 0,
    rewarded: false,
    chapter: chapter.index,
    journeyWave: s.journey.launched,
    firstPassEligible: true,
    members: [],
    defeatedMembers: [],
  });
  for (const [slot, [offsetX, offsetY, offsetZ]] of offsets.entries()) {
    const enemyClass = skySquadClass(chapter, s.sector, slot);
    const memberProfile = SKY_ENEMY_CLASSES[enemyClass];
    const kind = ['scout', 'bulwark'].includes(enemyClass) ? 'drone' : 'manta';
    const enemy = spawn(
      s,
      kind,
      centerX + offsetX,
      centerY + offsetY,
      -90 + offsetZ,
      wave.hp ?? memberProfile.hp,
    );
    Object.assign(enemy, memberProfile, {
      hp: wave.hp ?? memberProfile.hp,
      aggression: wave.aggression ?? memberProfile.aggression,
      attackPattern: wave.attackPattern ?? memberProfile.attackPattern,
      journeyMember: true,
      laneOriginY: s.journeyAltitude?.originY ?? 0,
      journeyClimbVelocity: s.journeyAltitude?.velocity ?? 0,
      offsetZ,
      enemyClass,
      speedScale: profile.speedScale * wave.speedMultiplier,
      formationAgility: profile.agility,
      approachSpeed: stage.speed * profile.speedScale * wave.speedMultiplier,
      squad,
      formation: chapter.formation,
      slot,
      offsetX,
      offsetY,
      centerX,
      centerY,
      phase,
      flightPath: chapter.flightPath,
      vx: 0,
      vy: 0,
      firedShots: 0,
    });
    s.squads.at(-1).members.push(enemy.id);
    s.enemies.push(enemy);
  }
  if (!s.journeyAltitude) s.enemies.push(...createSkyObstacles(s, chapter.index));
  return squad;
}
export function moveSkySquadMember(enemy, dt, speed) {
  const shift = skySquadDisplacement(enemy, enemy.age);
  const x = enemy.centerX + enemy.offsetX + shift.x;
  const y = enemy.centerY + enemy.offsetY + shift.y;
  enemy.vx = shift.vx;
  enemy.vy = shift.vy + (enemy.journeyClimbVelocity ?? 0);
  enemy.vz = speed * (enemy.speedScale ?? 1) + shift.vz;
  enemy.ax = shift.ax;
  enemy.ay = shift.ay;
  enemy.x = x;
  enemy.y = y;
  const previousDepth = skySquadDisplacement(enemy, Math.max(0, enemy.age - dt)).z;
  enemy.z += shift.z - previousDepth;
  enemy.formationBank = shift.bank;
  const remaining = (enemy.aggression ?? 1) - (enemy.firedShots ?? 0);
  const fireFront = -60 + (enemy.firedShots ?? 0) * 12;
  const charging = remaining > 0 && enemy.age >= 1.4 && enemy.z > fireFront - 18;
  enemy.chargeAge = charging ? (enemy.chargeAge ?? 0) + dt : 0;
  enemy.charge = clamp(enemy.chargeAge / 0.65, 0, 1);
  enemy.attackPhase = enemy.charge > 0 ? 'charge' : 'sweep';
}
